import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { LocalizedMessage } from 'intl/LocalizedComponents';
// Core components
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
import { TextButton } from 'components/core/Button';

// Models
import mUser from 'models/user';
// Custom
import {
  listRowSpecsShape,
  userDataShape,
  userCallbackDataShape,
} from 'constants/PropTypesShapes';
import { openDialog } from 'actions';
import { showPath } from 'users/navigation';
import withLoading from 'hocs/WithLoading';
import AppIcon from 'components/icons/AppIcon';
import { EditIconButton, DeleteIconButton } from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';

const UserCallback = ({ callback }) => {
  if (callback) {
    return (
      <div className="d-flex align-items-center">
        <Tooltip
          content={(
            <div>
              {callback.uri}
              {' '}
              (
              {callback.version}
              )
            </div>
          )}
        >
          <AppIcon defaultColor id="btnDisplay" />
        </Tooltip>
      </div>
    );
  }
  return null;
};

UserCallback.propTypes = {
  callback: userCallbackDataShape,
};

UserCallback.defaultProps = {
  callback: null,
};

const UserRollback = ({ user }) => {
  const { labelId, color, icon } = mUser.rollbackStrategy(user);

  return (
    <div className="d-flex align-items-center">
      <Tooltip
        content={(
          <div>
            <LocalizedMessage id={labelId} />
          </div>
        )}
      >
        <AppIcon color={color} id={icon} />
      </Tooltip>
    </div>
  );
};

UserRollback.propTypes = {
  user: userDataShape.isRequired,
};

const UserRow = React.forwardRef(({
  user, rowSpecs, handleStartLoading, onDelete, handleOpenDialog,
}, ref) => {
  const { id: userId } = user;

  // Handlers
  const handleDelete = () => {
    onDelete(user);
  };

  const handleDisplayPermissions = () => {
    handleOpenDialog({
      title: {
        id: 'title.userPermissions',
        values: { lastname: user.lastname, firstname: user.firstname },
      },
      actions: [{ type: 'close' }],
      component: {
        type: 'USER_PERMISSIONS',
        props: {
          permissions: user.permissions,
        },
      },
      size: 'md',
    });
  };

  return (
    <TableRow
      ref={ref}
      element={user}
      rowSpecs={rowSpecs}
      actions={[
        <EditIconButton key="bEdit" onClick={handleStartLoading} path={showPath(userId)} />,
        <DeleteIconButton key="bDelete" onClick={handleDelete} />,
      ]}
    >
      {/* ----- lastname -----  */}
      <TableCell className={classnames({ 'table-first-details': rowSpecs.withDetails })}>
        <TruncatedTooltipText>{user.lastname}</TruncatedTooltipText>
      </TableCell>
      {/* ----- firstname -----  */}
      <TableCell className="hidden-sm-down">
        <TruncatedTooltipText>{user.firstname}</TruncatedTooltipText>
      </TableCell>
      {/* ----- email -----  */}
      <TableCell className="hidden-md-down">
        <TruncatedTooltipText>{user.email}</TruncatedTooltipText>
      </TableCell>
      {/* ----- permissions -----  */}
      <TableCell className="hidden-md-down table-content-center">
        <TextButton
          inherit
          iconId="itemUserPermission"
          size="small"
          label={`${user.permissions.length}`}
          onClick={handleDisplayPermissions}
        />
      </TableCell>
      {/* ----- rollback -----  */}
      <TableCell className="hidden-md-down table-content-1 table-content-center">
        <UserRollback user={user} />
      </TableCell>
      {/* ----- callback -----  */}
      <TableCell className="hidden-md-down table-content-1 table-content-center" noPadding>
        <UserCallback callback={user.callback} />
      </TableCell>
    </TableRow>
  );
});

UserRow.displayName = 'UserRow';

UserRow.propTypes = {
  user: userDataShape.isRequired,
  rowSpecs: listRowSpecsShape.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
  handleOpenDialog: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

UserRow.defaultProps = {
  onDelete: null,
};

const mapDispatchToProps = (dispatch) => ({
  handleOpenDialog: (props) => {
    dispatch(openDialog(props));
  },
});

module.exports = compose(connect(null, mapDispatchToProps), withLoading)(UserRow);



// WEBPACK FOOTER //
// ./routes/app/routes/users/assets/main/UserRow.js